export const Permissions = {
  users: {
    create: "users:create",
    read: "users:read",
    update: "users:update",
    delete: "users:delete",
    ban: "users:ban",
  },
  audit: {
    read: "audit:read",
  },
  branch: {
    view: "branch.view",
    manage: "branch.manage",
    assign_user: "branch.assign_user",
    warehouse_manager: "branch.warehouse_manager",
    stock_controller: "branch.stock_controller",
    backup_approver: "branch.backup_approver",
  },
  stock: {
    view: "stock.view",
    manage: "stock.manage",
    adjust: "stock.adjust",
    movement_view: "stock.movement.view",
    lot_view: "stock.lot.view",
    transfer_view: "stock.transfer.view",
    transfer_create: "stock.transfer.create",
    transfer_complete: "stock.transfer.complete",
    transfer_receive: "stock.transfer.receive",
    transfer_cancel: "stock.transfer.cancel",
    service_view: "stock.service.view",
    service_manage: "stock.service.manage",
  },
  procurement: {
    view: "procurement.view",
    create: "procurement.create",
    approve: "procurement.approve",
    receive: "procurement.receive",
  },
  order: {
    view: "order.view",
    manage: "order.manage",
  },
  pos: {
    access: "pos.access",
    sell: "pos.sell",
    void: "pos.void",
    return: "pos.return",
    exchange: "pos.exchange",
    return_approve: "pos.return.approve",
    report_view: "pos.report.view",
    sell_expired: "pos.sell_expired",
  },
  sales_report: {
    view: "sales.report.view",
  },
  stock_report: {
    view: "stock.report.view",
    export: "stock.report.export",
    valuation_view: "stock.valuation.view",
  },
} as const;

export const ALL_PERMISSIONS = Object.entries(Permissions).flatMap(
  ([resource, actions]) =>
    Object.entries(actions).map(([action, id]) => ({ id, resource, action })),
);

export type PermissionId = (typeof ALL_PERMISSIONS)[number]["id"];

/** Permissions สำหรับ Stock module (ไม่รวม users/audit ของ platform) */
export const STOCK_MODULE_PERMISSION_IDS = ALL_PERMISSIONS.filter(
  (p) => p.resource !== "users" && p.resource !== "audit",
).map((p) => p.id);

const knownPermissionIds = new Set(ALL_PERMISSIONS.map((p) => p.id));

/** แยกสิทธิ์ที่ Stock จัดการใน UI กับสิทธิ์ Admin ERP ที่เก็บใน DB ร่วม */
export function partitionPermissions(ids: string[]): {
  managed: string[];
  external: string[];
} {
  const managed: string[] = [];
  const external: string[] = [];
  for (const id of ids) {
    if (knownPermissionIds.has(id)) managed.push(id);
    else external.push(id);
  }
  return { managed, external };
}

export function mergeRolePermissions(
  managed: string[],
  external: string[],
): string[] {
  return [...new Set([...external, ...managed])];
}

export function parsePermissionId(id: string): {
  resource: string;
  action: string;
} {
  if (id.includes(":")) {
    const parts = id.split(":");
    return { resource: parts[0] ?? "", action: parts.slice(1).join(":") };
  }

  const dot = id.indexOf(".");
  if (dot >= 0) {
    return { resource: id.slice(0, dot), action: id.slice(dot + 1) };
  }

  return { resource: id, action: "" };
}

// Human-friendly labels for rendering in UI
export const RESOURCE_LABELS: Record<string, string> = {
  users: "ຜູ້ໃຊ້",
  audit: "ບັນທຶກການກວດກາ",
  branch: "ສາຂາ",
  stock: "ສິນຄ້າ & ສາງ",
  procurement: "ຈັດຊື້",
  order: "ຄຳສັ່ງຂາຍ",
  pos: "POS",
  sales_report: "ລາຍງານຂາຍ",
  stock_report: "ລາຍງານສາງ",
};

export const ACTION_LABELS: Record<string, string> = {
  create: "ສ້າງ",
  read: "ເບິ່ງ",
  update: "ແກ້ໄຂ",
  delete: "ລຶບ",
  ban: "ລະງັບ",
  view: "ເບິ່ງ",
  manage: "ຈັດການ",
  assign_user: "ກຳນົດຜູ້ໃຊ້",
  warehouse_manager: "ຜູ້ຈັດການຄັງ",
  stock_controller: "ຜູ້ຄວບຄຸມສຕັອກ",
  backup_approver: "ຜູ້ອະນຸມັດສຳຮອງ",
  adjust: "ປັບຍອດ",
  movement_view: "ເບິ່ງການເຄື່ອນໄຫວ",
  lot_view: "ເບິ່ງ Lot",
  transfer_view: "ເບິ່ງໂອນສຕັອກ",
  transfer_create: "ສ້າງໃບໂອນ",
  transfer_complete: "ສົ່ງ/ຢືນຢັນໂອນ",
  transfer_receive: "ຮັບໂອນຂ້າມສາຂາ",
  transfer_cancel: "ຍົກເລີກໃບໂອນ",
  approve: "ອະນຸມັດ",
  receive: "ຮັບສິນຄ້າ",
  access: "ເຂົ້າໃຊ້",
  sell: "ຂາຍ",
  void: "ຍົກເລີກ",
  report_view: "ເບິ່ງລາຍງານ",
  sell_expired: "ຂາຍ Lot ໝົດອາຍຸ",
  export: "ສົ່ງອອກ",
  valuation_view: "ເບິ່ງມູນຄ່າສາງ",
};

/** ชื่อสิทธิ์เต็มสำหรับแสดงใน UI — key = permission id ใน DB */
export const PERMISSION_LABELS: Record<string, string> = {
  "users:create": "ສ້າງຜູ້ໃຊ້",
  "users:read": "ເບິ່ງຜູ້ໃຊ້",
  "users:update": "ແກ້ໄຂຜູ້ໃຊ້",
  "users:delete": "ລຶບຜູ້ໃຊ້",
  "users:ban": "ລະງັບຜູ້ໃຊ້",
  "audit:read": "ເບິ່ງບັນທຶກການກວດກາ",
  "branch.view": "ເບິ່ງສາຂາ",
  "branch.manage": "ຈັດການສາຂາ",
  "branch.assign_user": "ກຳນົດຜູ້ໃຊ້ເຂົ້າສາຂາ",
  "branch.warehouse_manager": "ສິດຜູ້ຈັດການຄັງສາຂາ",
  "branch.stock_controller": "ສິດຜູ້ຄວບຄຸມສຕັອກສາຂາ",
  "branch.backup_approver": "ສິດຜູ້ອະນຸມັດສຳຮອງ",
  "stock.view": "ເບິ່ງສິນຄ້າ & ສາງ",
  "stock.manage": "ຈັດການສິນຄ້າ & ສາງ",
  "stock.adjust": "ປັບຍອດສຕັອກ",
  "stock.movement_view": "ເບິ່ງການເຄື່ອນໄຫວສຕັອກ",
  "stock.lot_view": "ເບິ່ງ Lot / Batch",
  "stock.transfer_view": "ເບິ່ງໃບໂອນສຕັອກ",
  "stock.transfer_create": "ສ້າງໃບໂອນສຕັອກ",
  "stock.transfer_complete": "ຢືນຢັນ/ສົ່ງໃບໂອນ",
  "stock.transfer_receive": "ຮັບໂອນຂ້າມສາຂາ",
  "stock.transfer_cancel": "ຍົກເລີກໃບໂອນ",
  "stock.service.view": "ເບິ່ງບໍລິການ",
  "stock.service.manage": "ຈັດການບໍລິການ",
  "procurement.view": "ເບິ່ງຈັດຊື້",
  "procurement.create": "ສ້າງ PO",
  "procurement.approve": "ອະນຸມັດ PO",
  "procurement.receive": "ຮັບສິນຄ້າ (GRN)",
  "order.view": "ເບິ່ງຄຳສັ່ງຂາຍ",
  "order.manage": "ຈັດການຄຳສັ່ງຂາຍ",
  "pos.access": "ເຂົ້າໃຊ້ POS",
  "pos.sell": "ຂາຍ POS",
  "pos.void": "ຍົກເລີກບິນ POS",
  "pos.return": "ຄືນສິນຄ້າ POS",
  "pos.exchange": "ປ່ຽນສິນຄ້າ POS",
  "pos.return.approve": "ອະນຸມັດຄືນ/ປ່ຽນ POS",
  "pos.report_view": "ເບິ່ງລາຍງານ POS",
  "pos.sell_expired": "ຂາຍ Lot ໝົດອາຍຸ",
  "sales.report.view": "ເບິ່ງລາຍງານຂາຍ",
  "stock.report.view": "ເບິ່ງລາຍງານສາງ",
  "stock.report.export": "ສົ່ງອອກລາຍງານສາງ",
  "stock.report.valuation_view": "ເບິ່ງມູນຄ່າສາງ",
};

/** คำอธิบายเสริม (hint) สำหรับฟอร์ม — ไม่แสดง technical key */
export const PERMISSION_HINTS: Partial<Record<string, string>> = {
  "branch.warehouse_manager":
    "ເລືອກໄດ້ພຽງຜູ້ໃຊ້ທີ່ມີສິດ «ຜູ້ຈັດການຄັງສາຂາ» · ຮັບຜິດຊອບອະນຸມັດ PO ແລະ ໂອນອອກ",
  "branch.stock_controller":
    "ເລືອກໄດ້ພຽງຜູ້ໃຊ້ທີ່ມີສິດ «ຜູ້ຄວບຄຸມສຕັອກ» · ຮັບຂອງ ແລະ ດຳເນີນການໂອນປະຈຳວັນ",
  "branch.backup_approver":
    "ບໍ່ບັງຄັບ · ອະນຸມັດແທນເມື່ອຜູ້ຈັດການຄັງບໍ່ພ້ອມ",
};

export function getResourceLabel(resource: string): string {
  return RESOURCE_LABELS[resource] ?? resource;
}

export function getActionLabel(action: string): string {
  return ACTION_LABELS[action] ?? action.replaceAll(".", " ");
}

export function getPermissionLabel(id: PermissionId | string): string {
  const mapped = PERMISSION_LABELS[id];
  if (mapped) return mapped;
  const { resource, action } = parsePermissionId(id as string);
  const actionLabel = getActionLabel(action);
  const resourceLabel = getResourceLabel(resource);
  if (!actionLabel) return resourceLabel;
  if (actionLabel === resourceLabel) return actionLabel;
  return `${actionLabel} · ${resourceLabel}`;
}

export function getPermissionHint(id: PermissionId | string): string | undefined {
  return PERMISSION_HINTS[id];
}

export function getPermissionLabels(ids: PermissionId[]): string[] {
  return ids.map((id) => getPermissionLabel(id));
}
